import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import { getAdminOverview, updateAdminConfig, applyAdminRecommendation, adminSettleAll } from "../lib/api";
import { toast } from "sonner";
import { Shield, Save, Users, Activity, Zap, Coins, Wand2, Gavel } from "lucide-react";

export default function Admin() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    getAdminOverview()
      .then(d => {
        setData(d);
        setForm({
          refill_threshold: d.config.refill_threshold,
          refill_mode: d.config.refill_mode,
          refill_enabled: d.config.refill_enabled,
          xp_multiplier: d.config.xp_multiplier,
          last_90d_revenue_inr: d.config.last_90d_revenue_inr,
          reward_budget_pct: d.config.reward_budget_pct,
          quarterly_elite_pool_active: d.config.quarterly_elite_pool_active,
          annual_legend_pool_active: d.config.annual_legend_pool_active,
          tier_1_enabled: d.config.tier_1_enabled ?? true,
          tier_2_enabled: d.config.tier_2_enabled ?? true,
          tier_3_enabled: d.config.tier_3_enabled ?? true,
          // Economy v2 knobs
          starting_credits: d.config.starting_credits ?? 100000,
          recovery_amount: d.config.recovery_amount ?? 40000,
          recovery_cooldown_days: d.config.recovery_cooldown_days ?? 7,
          recovery_tax_rate: d.config.recovery_tax_rate ?? 0.20,
          recovery_tax_cap: d.config.recovery_tax_cap ?? 20000,
          prize_pool_return_pct: d.config.prize_pool_return_pct ?? 0.70,
          burn_pct: d.config.burn_pct ?? 0.30,
          tier_premium_enabled: d.config.tier_premium_enabled ?? true,
          tier_ultra_enabled: d.config.tier_ultra_enabled ?? true,
        });
      })
      .catch((e) => {
        if (e?.response?.status === 403) setForbidden(true);
      });
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await updateAdminConfig({
        ...form,
        last_90d_revenue_inr: Number(form.last_90d_revenue_inr),
        reward_budget_pct: Number(form.reward_budget_pct),
        refill_threshold: Number(form.refill_threshold),
        xp_multiplier: Number(form.xp_multiplier),
        starting_credits: Number(form.starting_credits),
        recovery_amount: Number(form.recovery_amount),
        recovery_cooldown_days: Number(form.recovery_cooldown_days),
        recovery_tax_rate: Number(form.recovery_tax_rate),
        recovery_tax_cap: Number(form.recovery_tax_cap),
        prize_pool_return_pct: Number(form.prize_pool_return_pct),
        burn_pct: Number(form.burn_pct),
      });
      toast.success("Admin settings updated.");
      setData(prev => ({ ...(prev || {}), config: r.config, recommendation: r.recommendation }));
      setForm(f => ({ ...f, ...r.config }));
    } catch {
      toast.error("Could not save");
    } finally { setBusy(false); }
  };

  const applyRec = async () => {
    setBusy(true);
    try {
      const r = await applyAdminRecommendation({
        last_90d_revenue_inr: Number(form.last_90d_revenue_inr),
        reward_budget_pct: Number(form.reward_budget_pct),
      });
      toast.success("Recommendation applied — tier toggles updated.");
      setData(prev => ({ ...(prev || {}), config: r.config, recommendation: r.recommendation }));
      setForm(f => ({ ...f, ...r.config }));
    } catch {
      toast.error("Could not apply");
    } finally { setBusy(false); }
  };

  const settleAll = async () => {
    setBusy(true);
    try {
      const r = await adminSettleAll();
      if (r.count > 0) toast.success(`Settled ${r.count} contest(s) — XP awarded to top-10.`);
      else toast("No due contests to settle.");
      const d = await getAdminOverview();
      setData(d);
    } catch {
      toast.error("Could not settle");
    } finally { setBusy(false); }
  };

  if (forbidden) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="glass rounded-2xl p-6 text-center">
          <Shield className="mx-auto w-8 h-8 text-[#FF0055]" />
          <p className="mt-3 text-white/80">Admin access required.</p>
        </div>
      </div>
    );
  }

  if (!data) return (
    <div className="min-h-screen pb-safe"><Header title="Admin" />
      <div className="max-w-xl mx-auto px-4 pt-6 text-white/60">Loading…</div>
    </div>
  );

  const rec = data.recommendation || {};
  return (
    <div className="min-h-screen pb-safe" data-testid="admin-page">
      <Header title="Admin" />
      <div className="max-w-xl mx-auto px-4 pt-4 space-y-4">
        <h1 className="font-display text-3xl font-bold">Admin Dashboard</h1>

        <div className="grid grid-cols-2 gap-3">
          <Stat icon={Users} label="Users" val={data.users} />
          <Stat icon={Activity} label="Trades" val={data.trades} />
          <Stat icon={Zap} label="Lineups" val={data.lineups} />
          <Stat icon={Coins} label="Claims" val={data.claims} />
        </div>

        <section className="glass rounded-2xl p-4 space-y-3">
          <h2 className="font-display text-lg font-bold">Anti-dead-account refills</h2>
          <Toggle label="Enable refills" checked={!!form.refill_enabled} onChange={v => setForm({ ...form, refill_enabled: v })} tid="refill-enabled" />
          <Row label="Refill mode">
            <select
              data-testid="refill-mode"
              value={form.refill_mode}
              onChange={e => setForm({ ...form, refill_mode: e.target.value })}
              className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-white"
            >
              <option value="daily_25k">Daily → ₹25,000</option>
              <option value="weekly_50k">Weekly → ₹50,000</option>
              <option value="off">Off</option>
            </select>
          </Row>
          <Row label="Threshold">
            <input type="number" data-testid="refill-threshold" value={form.refill_threshold} onChange={e => setForm({ ...form, refill_threshold: e.target.value })} className="w-32 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
          </Row>
        </section>

        <section className="glass rounded-2xl p-4 space-y-3">
          <h2 className="font-display text-lg font-bold">XP economy</h2>
          <Row label="XP multiplier">
            <input type="number" step="0.1" min="0.1" data-testid="xp-multiplier" value={form.xp_multiplier} onChange={e => setForm({ ...form, xp_multiplier: e.target.value })} className="w-24 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
          </Row>
        </section>

        <section className="glass rounded-2xl p-4 space-y-3" data-testid="admin-economy-v2">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <Coins className="w-4 h-4 text-[#FFD600]" /> Economy v2 (credits-first)
          </h2>
          <p className="text-[11px] text-white/55 leading-relaxed">
            Master knobs for the 1-layer Fanpuff Credits economy. Changes apply to all future contest settlements & recovery claims.
          </p>
          <Row label="Starting credits">
            <input type="number" data-testid="starting-credits" value={form.starting_credits ?? ""} onChange={e => setForm({ ...form, starting_credits: e.target.value })} className="w-32 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
          </Row>
          <Row label="Prize pool return %">
            <input type="number" step="0.05" min="0" max="1" data-testid="prize-pool-pct" value={form.prize_pool_return_pct ?? ""} onChange={e => setForm({ ...form, prize_pool_return_pct: e.target.value })} className="w-24 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
          </Row>
          <Row label="Burn % (sink)">
            <input type="number" step="0.05" min="0" max="1" data-testid="burn-pct" value={form.burn_pct ?? ""} onChange={e => setForm({ ...form, burn_pct: e.target.value })} className="w-24 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
          </Row>
          <p className="text-[10px] text-white/45">Return + Burn should sum to 1.0 (current: {((Number(form.prize_pool_return_pct || 0) + Number(form.burn_pct || 0)) || 0).toFixed(2)}).</p>

          <div className="pt-2 border-t border-white/10">
            <p className="text-[10px] tracking-widest uppercase font-bold text-white/55 mb-2">Recovery Pack (bankrupt fallback)</p>
            <Row label="Recovery threshold">
              <input type="number" data-testid="recovery-threshold" value={form.refill_threshold ?? ""} onChange={e => setForm({ ...form, refill_threshold: e.target.value })} className="w-32 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
            </Row>
            <Row label="Recovery amount">
              <input type="number" data-testid="recovery-amount" value={form.recovery_amount ?? ""} onChange={e => setForm({ ...form, recovery_amount: e.target.value })} className="w-32 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
            </Row>
            <Row label="Cooldown (days)">
              <input type="number" data-testid="recovery-cooldown" value={form.recovery_cooldown_days ?? ""} onChange={e => setForm({ ...form, recovery_cooldown_days: e.target.value })} className="w-20 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
            </Row>
            <Row label="Tax rate (0–1)">
              <input type="number" step="0.05" min="0" max="1" data-testid="recovery-tax-rate" value={form.recovery_tax_rate ?? ""} onChange={e => setForm({ ...form, recovery_tax_rate: e.target.value })} className="w-24 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
            </Row>
            <Row label="Tax cap (credits)">
              <input type="number" data-testid="recovery-tax-cap" value={form.recovery_tax_cap ?? ""} onChange={e => setForm({ ...form, recovery_tax_cap: e.target.value })} className="w-32 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
            </Row>
          </div>

          <div className="pt-2 border-t border-white/10 space-y-2">
            <p className="text-[10px] tracking-widest uppercase font-bold text-white/55">Premium-tier master toggles</p>
            <Toggle label="Premium tier rewards (3M+) claimable" checked={!!form.tier_premium_enabled} onChange={v => setForm({ ...form, tier_premium_enabled: v })} tid="tier-premium-toggle" />
            <Toggle label="Ultra tier rewards (8M+) claimable" checked={!!form.tier_ultra_enabled} onChange={v => setForm({ ...form, tier_ultra_enabled: v })} tid="tier-ultra-toggle" />
          </div>
        </section>

        <section className="glass rounded-2xl p-4 space-y-3">
          <h2 className="font-display text-lg font-bold">Reward budget</h2>
          <Row label="Last-90-day revenue (₹)">
            <input type="number" data-testid="rev-input" value={form.last_90d_revenue_inr} onChange={e => setForm({ ...form, last_90d_revenue_inr: e.target.value })} className="w-40 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
          </Row>
          <Row label="Budget %">
            <input type="number" min="0" max="100" data-testid="budget-pct" value={form.reward_budget_pct} onChange={e => setForm({ ...form, reward_budget_pct: e.target.value })} className="w-24 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
          </Row>
          <div className="text-xs text-white/65 glass rounded-xl p-3">
            <p className="font-bold text-white/80">Recommended tier gating (from budget)</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              <Rec ok={rec.allow_tier_1} label="Tier 1 (₹299–₹499)" />
              <Rec ok={rec.allow_tier_2} label="Tier 2 (₹799–₹1499)" />
              <Rec ok={rec.allow_tier_3} label="Tier 3 (₹2k–₹4k)" />
              <Rec ok={rec.allow_elite} label="Elite Pool" />
              <Rec ok={rec.allow_legend} label="Legend Pool" />
            </div>
            <p className="mt-2">Computed budget: <b className="text-white">₹{(rec.budget_inr || 0).toLocaleString("en-IN")}</b></p>
            <button
              type="button"
              onClick={applyRec}
              disabled={busy}
              data-testid="apply-rec-btn"
              className="mt-3 w-full py-2 rounded-full font-bold text-black inline-flex items-center justify-center gap-2 text-sm"
              style={{ background: "linear-gradient(90deg,#00FF66,#00F0FF)" }}
            >
              <Wand2 className="w-4 h-4" /> Apply recommendation to toggles
            </button>
          </div>
          <div className="space-y-2 pt-1">
            <p className="text-[10px] tracking-widest uppercase font-bold text-white/55">Tier master toggles (gate claims app-wide)</p>
            <Toggle label="Tier 1 rewards (₹299–₹499) claimable" checked={!!form.tier_1_enabled} onChange={v => setForm({ ...form, tier_1_enabled: v })} tid="tier-1-toggle" />
            <Toggle label="Tier 2 rewards (₹799–₹1,499) claimable" checked={!!form.tier_2_enabled} onChange={v => setForm({ ...form, tier_2_enabled: v })} tid="tier-2-toggle" />
            <Toggle label="Tier 3 rewards (₹2,000–₹4,000) claimable" checked={!!form.tier_3_enabled} onChange={v => setForm({ ...form, tier_3_enabled: v })} tid="tier-3-toggle" />
          </div>
          <Toggle label="Quarterly Elite pool active" checked={!!form.quarterly_elite_pool_active} onChange={v => setForm({ ...form, quarterly_elite_pool_active: v })} tid="elite-pool-toggle" />
          <Toggle label="Annual Legend pool active" checked={!!form.annual_legend_pool_active} onChange={v => setForm({ ...form, annual_legend_pool_active: v })} tid="legend-pool-toggle" />
        </section>

        <button
          onClick={save}
          disabled={busy}
          data-testid="admin-save-btn"
          className="w-full py-3 rounded-full font-bold text-black inline-flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF)" }}
        >
          <Save className="w-4 h-4" /> Save settings
        </button>

        <section className="glass rounded-2xl p-4 space-y-3">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <Gavel className="w-4 h-4 text-[#FFD600]" /> Contest auto-settlement
          </h2>
          <p className="text-xs text-white/60 leading-relaxed">
            When a contest's <b>end_date</b> passes, Fanpuff auto-awards XP to the top-10 lineups: <b>#1 +2,000 · #2 +1,500 · #3 +1,000 · #4–5 +600 · #6–7 +500 · #8–10 +400</b>. Runs every 5 minutes in the background. Force a run below.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="glass rounded-xl p-3">
              <p className="text-[10px] tracking-widest uppercase text-white/50">Settled</p>
              <p className="font-display text-xl font-semibold" data-testid="settled-count">{data.settled_contests ?? 0}</p>
            </div>
            <div className="glass rounded-xl p-3">
              <p className="text-[10px] tracking-widest uppercase text-white/50">Due — unsettled</p>
              <p className="font-display text-xl font-semibold" style={{ color: (data.pending_settle || 0) > 0 ? "#FFD600" : "#fff" }} data-testid="pending-settle-count">{data.pending_settle ?? 0}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={settleAll}
            disabled={busy}
            data-testid="admin-settle-all-btn"
            className="w-full py-2.5 rounded-full font-bold text-black inline-flex items-center justify-center gap-2 text-sm"
            style={{ background: "linear-gradient(90deg,#FFD600,#FF0055)" }}
          >
            <Gavel className="w-4 h-4" /> Settle due contests now
          </button>
        </section>

        <section className="glass rounded-2xl p-4">
          <h2 className="font-display text-lg font-bold">Top XP (latest snapshot)</h2>
          <div className="mt-2 space-y-1 text-sm">
            {(data.top_xp || []).map((u, i) => (
              <div key={i} className="flex items-center justify-between">
                <span>{u.name}</span>
                <span className="text-white/60 font-mono-brand">{(u.xp || 0).toLocaleString("en-IN")} XP · {u.tier}</span>
              </div>
            ))}
          </div>
        </section>

        <div className="h-10" />
      </div>
      <BottomNav />
    </div>
  );
}

function Stat({ icon: Icon, label, val }) {
  return (
    <div className="glass rounded-2xl p-3">
      <div className="flex items-center gap-1 text-[10px] tracking-widest uppercase text-white/50">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className="font-display text-lg font-semibold mt-1">{(val || 0).toLocaleString("en-IN")}</p>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs text-white/60 uppercase tracking-widest font-bold">{label}</p>
      {children}
    </div>
  );
}
function Toggle({ label, checked, onChange, tid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={tid}
      className="w-full flex items-center justify-between glass rounded-xl px-3 py-2"
    >
      <span className="text-sm">{label}</span>
      <span className={`w-10 h-6 rounded-full transition ${checked ? "" : "bg-white/10"}`} style={checked ? { background: "linear-gradient(90deg,#00F0FF,#B026FF)" } : {}}>
        <span className={`block w-5 h-5 rounded-full bg-white translate-y-0.5 transition ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
}
function Rec({ ok, label }) {
  return (
    <span className="chip" style={{ background: ok ? "#00FF6622" : "rgba(255,255,255,0.05)", color: ok ? "#00FF66" : "rgba(255,255,255,0.55)", border: `1px solid ${ok ? "#00FF6655" : "rgba(255,255,255,0.1)"}` }}>
      {ok ? "OK" : "Low"} · {label}
    </span>
  );
}
